import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  Armchair,
  CheckCircle2,
  Circle,
  PlugZap,
  ReceiptText,
  Sparkles,
  UtensilsCrossed,
  Wallet,
} from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { RealtimeBadge } from "../components/RealtimeBadge";
import { StatusBadge } from "../components/StatusBadge";
import { useRealtime } from "../lib/realtime";
import {
  api,
  ApiRequestError,
  type Me,
  type OverviewResponse,
} from "../lib/api";
import { formatDateTime, formatRupiah } from "../lib/format";

const checklist = [
  {
    key: "outlet",
    label: "Outlet aktif",
    description: "Minimal satu outlet siap menerima pesanan.",
    to: "/app/settings",
  },
  {
    key: "menu",
    label: "Menu siap dijual",
    description: "Kategori, item, dan harga sudah diisi.",
    to: "/app/menu",
  },
  {
    key: "webqris",
    label: "WebQRIS terhubung",
    description: "Merchant, token, dan webhook sudah diverifikasi.",
    to: "/app/integrations",
  },
  {
    key: "whatsapp",
    label: "WhatsApp Gateway terhubung",
    description: "Device WA siap mengirim struk dan status.",
    to: "/app/integrations",
  },
] as const;

export function OverviewPage({ me }: { me: Me }) {
  const [data, setData] = useState<OverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const overview = await api<OverviewResponse>("/api/owner/overview");
      setData(overview);
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Tidak dapat memuat ringkasan.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const realtime = useRealtime({
    staff: true,
    onEvent: () => {
      void load();
    },
  });

  const firstName = me.user.name.split(/\s+/)[0] ?? me.user.name;
  const checks = {
    outlet: (data?.counts.outlets ?? 0) > 0,
    menu: (data?.counts.menuItems ?? 0) > 0,
    webqris: data?.integrations.webqris === "active",
    whatsapp: data?.integrations.whatsapp === "active",
  };
  const completed = Object.values(checks).filter(Boolean).length;
  const billing = data?.billing ?? null;
  const paidBilling = billing?.billingType === "paid";
  const feeActive = Boolean(
    billing && paidBilling && billing.feeActive && billing.feeAmount > 0,
  );
  const lowBalance = Boolean(
    billing && feeActive && billing.balance < billing.feeAmount,
  );
  const coveredOrders = billing && feeActive
    ? Math.max(0, Math.floor(billing.balance / billing.feeAmount))
    : 0;
  const billingTone = !paidBilling
    ? "free"
    : lowBalance
      ? "warning"
      : "success";
  const billingStatus = !paidBilling
    ? "Bebas fee"
    : lowBalance
      ? "Perlu top-up"
      : "Saldo aman";
  const billingNote = !paidBilling
    ? "Billing khusus · tidak ada potongan per transaksi"
    : !feeActive
      ? "Tidak ada fee aktif saat ini"
      : lowBalance
        ? `Saldo kurang dari fee ${formatRupiah(billing.feeAmount)} per order`
        : `Cukup untuk ${coveredOrders} transaksi · fee ${formatRupiah(billing.feeAmount)}/order`;

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Hari ini"
        title={`Selamat datang, ${firstName}`}
        description={`Ringkasan ${me.tenant.name}. Semua yang penting, tanpa harus menunggu tutup toko.`}
        actions={
          <div className="kds-actions">
            <RealtimeBadge connected={realtime.connected} />
            <Link className="btn btn-secondary" to="/app/integrations">
              <PlugZap size={16} />
              Kelola integrasi
            </Link>
          </div>
        }
      />

      {error ? (
        <div className="alert alert--danger" role="alert">
          <AlertCircle size={17} />
          <span>{error}</span>
        </div>
      ) : null}

      {me.membership.role === "owner" && billing ? (
        <Link
          to="/app/billing"
          className={`billing-strip billing-strip--${billingTone}`}
          aria-label="Buka billing dan deposit"
        >
          <span className="billing-strip__icon">
            <Wallet size={19} />
          </span>
          <span className="billing-strip__main">
            <span className="billing-strip__label">Saldo deposit</span>
            <strong>{formatRupiah(billing.balance)}</strong>
            <small>{billingNote}</small>
          </span>
          <span className="billing-strip__status">{billingStatus}</span>
          <span className="billing-strip__cta">
            Kelola <ArrowRight size={15} />
          </span>
        </Link>
      ) : null}

      <section className="stat-grid" aria-label="Ringkasan hari ini">
        <article className="stat-card">
          <span className="stat-card__icon stat-card__icon--accent">
            <Wallet size={18} />
          </span>
          <div>
            <p className="stat-card__label">Omzet hari ini</p>
            <strong className="stat-card__value">
              {loading ? (
                <span className="skeleton skeleton--value" />
              ) : (
                formatRupiah(data?.counts.revenueToday ?? 0)
              )}
            </strong>
            <span className="stat-card__note">
              {data?.counts.paidOrdersToday ?? 0} pesanan sudah dibayar
            </span>
          </div>
        </article>

        <article className="stat-card">
          <span className="stat-card__icon">
            <ReceiptText size={18} />
          </span>
          <div>
            <p className="stat-card__label">Pesanan hari ini</p>
            <strong className="stat-card__value">
              {loading ? (
                <span className="skeleton skeleton--value" />
              ) : (
                (data?.counts.ordersToday ?? 0)
              )}
            </strong>
            <span className="stat-card__note">Masuk dari semua channel</span>
          </div>
        </article>

        <article className="stat-card">
          <span className="stat-card__icon">
            <UtensilsCrossed size={18} />
          </span>
          <div>
            <p className="stat-card__label">Menu aktif</p>
            <strong className="stat-card__value">
              {loading ? (
                <span className="skeleton skeleton--value" />
              ) : (
                (data?.counts.menuItems ?? 0)
              )}
            </strong>
            <span className="stat-card__note">
              Siap ditampilkan ke pelanggan
            </span>
          </div>
        </article>

        <article className="stat-card">
          <span className="stat-card__icon">
            <Armchair size={18} />
          </span>
          <div>
            <p className="stat-card__label">Meja & kamar</p>
            <strong className="stat-card__value">
              {loading ? (
                <span className="skeleton skeleton--value" />
              ) : (
                (data?.counts.units ?? 0)
              )}
            </strong>
            <span className="stat-card__note">Unit dengan QR aktif</span>
          </div>
        </article>
      </section>

      <div className="overview-grid">
        <section className="card card--pad">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Go-live</p>
              <h2>Siapkan fondasi operasional</h2>
              <p className="section-subtitle">
                {completed} dari {checklist.length} langkah selesai.
              </p>
            </div>
            <span className="progress-pill">
              {Math.round((completed / checklist.length) * 100)}%
            </span>
          </div>

          <div className="checklist">
            {checklist.map((item) => {
              const done = checks[item.key];
              return (
                <Link
                  key={item.key}
                  to={item.to}
                  className={`checklist-item${done ? " is-done" : ""}`}
                >
                  <span className="checklist-item__mark">
                    {done ? <CheckCircle2 size={19} /> : <Circle size={19} />}
                  </span>
                  <span className="checklist-item__copy">
                    <strong>{item.label}</strong>
                    <span>{item.description}</span>
                  </span>
                  <ArrowRight size={16} className="checklist-item__arrow" />
                </Link>
              );
            })}
          </div>
        </section>

        <aside className="card card--pad overview-side">
          <div className="section-heading section-heading--compact">
            <div>
              <p className="eyebrow">Koneksi</p>
              <h2>Status integrasi</h2>
            </div>
            <Sparkles size={18} className="muted-icon" />
          </div>

          <div className="integration-status">
            <div className="integration-status__row">
              <div>
                <strong>WebQRIS</strong>
                <span>Pembayaran QRIS</span>
              </div>
              <StatusBadge
                status={data?.integrations.webqris ?? "not_configured"}
              />
            </div>
            <div className="integration-status__row">
              <div>
                <strong>WhatsApp Gateway</strong>
                <span>Struk & status pesanan</span>
              </div>
              <StatusBadge
                status={data?.integrations.whatsapp ?? "not_configured"}
              />
            </div>
          </div>

          <Link className="btn btn-primary btn-block" to="/app/integrations">
            Buka pengaturan integrasi
            <ArrowRight size={16} />
          </Link>

          <p className="fine-print">
            Secret disimpan terenkripsi. Hanya owner yang dapat mengubahnya.
          </p>
        </aside>
      </div>

      <section className="card card--pad">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Aktivitas</p>
            <h2>Pesanan terbaru</h2>
          </div>
          <Link className="text-link" to="/app/orders">
            Lihat semua <ArrowRight size={14} />
          </Link>
        </div>

        {loading ? (
          <div className="overview-order-list overview-order-list--loading">
            <span className="skeleton skeleton--block" />
            <span className="skeleton skeleton--block" />
            <span className="skeleton skeleton--block" />
          </div>
        ) : data?.recentOrders.length ? (
          <div className="overview-order-list">
            {data.recentOrders.map((order) => (
              <Link
                className="overview-order"
                to="/app/orders"
                key={order.id}
              >
                <span className="overview-order__main">
                  <strong>
                    #{order.orderNo ?? order.id.slice(0, 8)}
                  </strong>
                  <small>{formatDateTime(order.createdAt)}</small>
                </span>
                <span className="overview-order__amount">
                  {formatRupiah(order.total)}
                </span>
                <span className="overview-order__badges">
                  <StatusBadge status={order.paymentStatus} />
                  <StatusBadge status={order.fulfillmentStatus} />
                </span>
              </Link>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <span className="empty-state__icon">
              <ReceiptText size={24} />
            </span>
            <h3>Belum ada pesanan</h3>
            <p>
              Setelah QR meja aktif dan pelanggan memesan, pesanan akan muncul
              di sini secara real-time.
            </p>
            <Link className="btn btn-secondary" to="/app/tables">
              Siapkan meja & QR
              <ArrowRight size={15} />
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
